import { useState, useMemo } from 'react';
import { WorkflowResult } from '../api/client';
import SellerDetailsModal from './SellerDetailsModal';

interface ResultsTableProps {
  results: WorkflowResult;
}

type SortField = 'upc' | 'productTitle' | 'sellerUsername' | 'qualificationStatus';
type SortDirection = 'asc' | 'desc';
type ModalCategory = 'qualified' | 'unqualified' | 'noResults' | 'errors' | null;
type ViewTab = 'qualified' | 'unqualified';

const ITEMS_PER_PAGE = 15;

export default function ResultsTable({ results }: ResultsTableProps) {
  const [sortField, setSortField] = useState<SortField>('upc');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [modalCategory, setModalCategory] = useState<ModalCategory>(null);
  const [currentTab, setCurrentTab] = useState<ViewTab>('qualified');
  const [currentPage, setCurrentPage] = useState(1);
  const [upcFilter, setUpcFilter] = useState('');
  const [sellerFilter, setSellerFilter] = useState('');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter results based on current tab
  const filteredByTab = useMemo(() => {
    return results.results.filter(r => {
      if (currentTab === 'qualified') {
        return r.qualificationStatus === 'Qualified';
      } else {
        return r.qualificationStatus === 'Unqualified';
      }
    });
  }, [results.results, currentTab]);

  // Apply UPC and seller filters
  const filteredResults = useMemo(() => {
    return filteredByTab.filter(r => {
      const matchesUPC = !upcFilter || r.upc.toLowerCase().includes(upcFilter.toLowerCase());
      const matchesSeller = !sellerFilter || r.sellerUsername.toLowerCase().includes(sellerFilter.toLowerCase());
      return matchesUPC && matchesSeller;
    });
  }, [filteredByTab, upcFilter, sellerFilter]);

  // Sort filtered results
  const sortedResults = useMemo(() => {
    return [...filteredResults].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredResults, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedResults.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedResults = sortedResults.slice(startIndex, endIndex);

  // Reset to page 1 when filters or tab changes
  const handleTabChange = (tab: ViewTab) => {
    setCurrentTab(tab);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType: 'upc' | 'seller', value: string) => {
    if (filterType === 'upc') {
      setUpcFilter(value);
    } else {
      setSellerFilter(value);
    }
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    const headers = ['UPC', 'Product Title', 'Item URL', 'Seller Username', 'Status', 'Outreach Message'];
    const rows = results.results.map(r => [
      r.upc,
      r.productTitle,
      r.itemUrl,
      r.sellerUsername,
      r.qualificationStatus,
      r.outreachMessage,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ebay-seller-scout-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusClass = (status: string | undefined) => {
    if (!status) return 'status-error';
    
    switch (status.toLowerCase()) {
      case 'qualified':
        return 'status-qualified';
      case 'unqualified':
        return 'status-unqualified';
      case 'no results':
        return 'status-no-results';
      case 'error':
        return 'status-error';
      default:
        return '';
    }
  };

  const getFilteredSellers = (category: ModalCategory) => {
    if (!category) return [];
    
    switch (category) {
      case 'qualified':
        return results.results.filter(r => r.qualificationStatus === 'Qualified');
      case 'unqualified':
        return results.results.filter(r => r.qualificationStatus === 'Unqualified');
      case 'noResults':
        return results.results.filter(r => r.qualificationStatus === 'No Results');
      case 'errors':
        return results.results.filter(r => r.qualificationStatus === 'Error');
      default:
        return [];
    }
  };

  const handleCardClick = (category: ModalCategory) => {
    setModalCategory(category);
  };

  const handleCloseModal = () => {
    setModalCategory(null);
  };

  return (
    <div className="results-section">
      <div className="results-header">
        <h2>Results</h2>
        <button className="download-btn" onClick={downloadCSV}>
          📥 Download CSV
        </button>
      </div>

      <div className="summary-stats">
        <div className="stat-card">
          <h3>{results.summary.totalUPCs}</h3>
          <p>Total UPCs</p>
        </div>
        <div className="stat-card clickable" onClick={() => handleCardClick('qualified')}>
          <h3>{results.summary.qualifiedSellers}</h3>
          <p>Qualified Sellers</p>
          <span className="card-hint">Click to view details</span>
        </div>
        <div className="stat-card">
          <h3>{results.summary.messagesGenerated}</h3>
          <p>Messages Generated</p>
        </div>
        <div className="stat-card clickable" onClick={() => handleCardClick('unqualified')}>
          <h3>{results.summary.unqualifiedSellers}</h3>
          <p>Unqualified</p>
          <span className="card-hint">Click to view details</span>
        </div>
        <div className="stat-card clickable" onClick={() => handleCardClick('noResults')}>
          <h3>{results.summary.noResults}</h3>
          <p>No Results</p>
          <span className="card-hint">Click to view details</span>
        </div>
        <div className="stat-card clickable" onClick={() => handleCardClick('errors')}>
          <h3>{results.summary.errors}</h3>
          <p>Errors</p>
          <span className="card-hint">Click to view details</span>
        </div>
      </div>

      {/* Tabs for Qualified/Unqualified */}
      <div className="view-tabs">
        <button 
          className={`tab-button ${currentTab === 'qualified' ? 'active' : ''}`}
          onClick={() => handleTabChange('qualified')}
        >
          ✅ Qualified Sellers ({filteredByTab.length > 0 && currentTab === 'qualified' ? filteredByTab.length : results.results.filter(r => r.qualificationStatus === 'Qualified').length})
        </button>
        <button 
          className={`tab-button ${currentTab === 'unqualified' ? 'active' : ''}`}
          onClick={() => handleTabChange('unqualified')}
        >
          ❌ Unqualified Sellers ({filteredByTab.length > 0 && currentTab === 'unqualified' ? filteredByTab.length : results.results.filter(r => r.qualificationStatus === 'Unqualified').length})
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="upc-filter">🔍 Filter by UPC:</label>
          <input
            id="upc-filter"
            type="text"
            value={upcFilter}
            onChange={(e) => handleFilterChange('upc', e.target.value)}
            placeholder="Enter UPC..."
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="seller-filter">👤 Filter by Seller:</label>
          <input
            id="seller-filter"
            type="text"
            value={sellerFilter}
            onChange={(e) => handleFilterChange('seller', e.target.value)}
            placeholder="Enter seller username..."
            className="filter-input"
          />
        </div>
        {(upcFilter || sellerFilter) && (
          <button 
            className="clear-filters-btn"
            onClick={() => {
              setUpcFilter('');
              setSellerFilter('');
              setCurrentPage(1);
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="results-table">
        {paginatedResults.length === 0 ? (
          <div className="empty-results">
            <p>No {currentTab} sellers found{(upcFilter || sellerFilter) ? ' matching your filters' : ''}.</p>
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('upc')}>
                    UPC {sortField === 'upc' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('productTitle')}>
                    Product Title {sortField === 'productTitle' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('sellerUsername')}>
                    Seller {sortField === 'sellerUsername' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('qualificationStatus')}>
                    Status {sortField === 'qualificationStatus' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  {currentTab === 'qualified' && <th>Outreach Message</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedResults.map((result, index) => (
                  <tr key={`${result.upc || 'unknown'}-${result.sellerUsername || 'unknown'}-${index}`}>
                    <td>{result.upc || 'N/A'}</td>
                    <td>
                      <a href={result.itemUrl || '#'} target="_blank" rel="noopener noreferrer" className="link">
                        {result.productTitle || 'N/A'}
                      </a>
                    </td>
                    <td>{result.sellerUsername || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(result.qualificationStatus)}`}>
                        {result.qualificationStatus || 'Unknown'}
                      </span>
                    </td>
                    {currentTab === 'qualified' && <td className="message-cell">{result.outreachMessage || ''}</td>}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  ⏮ First
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ◀ Prev
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages} ({sortedResults.length} total items)
                </span>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next ▶
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last ⏭
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <SellerDetailsModal
        isOpen={modalCategory !== null}
        onClose={handleCloseModal}
        category={modalCategory || 'qualified'}
        sellers={getFilteredSellers(modalCategory)}
      />
    </div>
  );
}


